import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DateTime } from 'luxon';
import { DiaryFirstBookings, FirstBooking } from '@models/first-timeslot.model';
import { Practice } from '@models/practice.model';
import { AlertService } from '@services/alert.service';
import { AuthService } from '@services/auth.service';
import { DisplayService } from '@services/display.service';
import { PracticeService } from '@services/practice.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, finalize, tap, timeout } from 'rxjs/operators';
import { getDeviceTimezone } from 'src/app/timezones';

@Component({
  templateUrl: './practice-booking.component.html',
  styleUrls: ['./practice-booking.component.scss'],
})
export class PracticeBookingComponent implements OnInit {
  error = { hasError: undefined, message: '' };
  authenticated$: Observable<boolean>;
  isMobile$: BehaviorSubject<boolean>;
  timeslotDiaries: DiaryFirstBookings[] = [];
  practice: Practice;
  bookingTypeSelectors: FormGroup = this.formBuilder.group({});
  theme: {
    primary: string;
    accent: string;
  };

  constructor(
    private readonly practiceService: PracticeService,
    private readonly auth: AuthService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    public readonly display: DisplayService,
    private readonly alert: AlertService,
    private readonly formBuilder: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.isMobile$ = this.display.isMobile$;
    this.authenticated$ = this.auth.authenticated$;
    this.getPracticeAndDiaryInfo();
  }

  // sorts by timeslot
  sortDiariesByTimeSlot(diaries: DiaryFirstBookings[]) {
    const earliestTimeSlot = (bookingType: FirstBooking): Date =>
      DateTime.fromISO((bookingType.timeslots?.[0] ?? '').toString()).toJSDate();
    const sortBookingTypesByTimeSlot = (diary: DiaryFirstBookings) => {
      diary.booking_types.sort((t1, t2) => earliestTimeSlot(t1).getTime() - earliestTimeSlot(t2).getTime());
    };

    diaries.sort((diary1, diary2) => {
      // Sort booking types of each diary based on earliest timeslot
      sortBookingTypesByTimeSlot(diary1);
      sortBookingTypesByTimeSlot(diary2);
      // Find the earliest time slot in the first booking type of diary A and B
      const earliestTimeSlotA = earliestTimeSlot(diary1.booking_types[0]);
      const earliestTimeSlotB = earliestTimeSlot(diary2.booking_types[0]);
      return earliestTimeSlotA.getTime() - earliestTimeSlotB.getTime();
    });

    // filter out diaries where all of their timeslots are empty
    diaries = diaries.filter((diary) => diary.booking_types.some((bookingType) => bookingType.timeslots.length > 0));
    return diaries;
  }

  getPracticeAndDiaryInfo() {
    this.error = { hasError: undefined, message: '' };
    const identifier = this.route.snapshot.paramMap.get('identifier');
    this.practiceService
      .getPracticeTimeslots$(identifier)
      .pipe(
        timeout(10_000), //add timeout after 10 seconds.
        catchError(() => of(new Error("Sorry, we're having trouble fetching data. Please try again later."))),
      )
      .subscribe((diaries: DiaryFirstBookings[] | Error) => {
        if (diaries instanceof Error) {
          this.error.hasError = true;
          this.error.message = diaries.message;
          this.timeslotDiaries = [];
        } else {
          diaries.forEach((diary) => {
            const selectFormControl = new FormControl(diary.booking_types?.[0]);
            this.bookingTypeSelectors.addControl(diary.diary_uuid, selectFormControl);
          });
          this.timeslotDiaries = this.sortDiariesByTimeSlot(diaries);
          this.error.hasError = false;
        }
      });

    this.practiceService
      .getPractice$(identifier)
      .pipe(catchError(() => of(new Error("Sorry, we're having trouble fetching data. Please try again later."))))
      .subscribe((practice: Practice | Error) => {
        if (practice instanceof Error) {
          this.error.hasError = true;
          this.error.message = practice.message;
        } else {
          this.practice = practice;
          this.theme = this.practice.theme;
          if (this.theme?.primary && this.theme?.accent) {
            this.display.changeTheme(this.theme.primary, this.theme.accent);
          }
        }
      });
  }

  logout() {
    this.alert
      .confirmDialog$('Logout', { detail: 'Are you sure you wish to logout?' })
      .pipe(
        filter((result) => !!result),
        tap(() => {
          this.auth
            .logout()
            .pipe(finalize(() => window.location.reload()))
            .subscribe();
        }),
      )
      .subscribe();
  }

  comparisonFn(bookingType1: FirstBooking, bookingType2: FirstBooking) {
    return bookingType1 && bookingType2 && bookingType1.uuid === bookingType2.uuid;
  }

  getFirstTimeslot(diaryUuid: string) {
    const doctorTimezone = this.getDiary(diaryUuid).timezone_json?.timezone;
    const bookingType = this.bookingTypeSelectors.get(diaryUuid).value as FirstBooking;
    if (getDeviceTimezone() === doctorTimezone) {
      return bookingType.timeslots.length ? DateTime.fromISO(bookingType.timeslots[0].toString()) : undefined;
    }

    return this.convertToTimezone(bookingType.timeslots[0], doctorTimezone);
  }

  timeslotByDiaryUuid(diaryUuid: string) {
    const timeslot: DateTime = this.getFirstTimeslot(diaryUuid);
    if (!timeslot || !timeslot.isValid) {
      return 'No available timeslots';
    }
    return timeslot.toFormat('HH:mm ccc LLL d yyyy ZZ');
  }

  convertToTimezone(date, doctorTimezone) {
    return DateTime.fromISO(date, { zone: doctorTimezone }).setZone(getDeviceTimezone());
  }

  get practiceName() {
    return this.practice?.name ?? '';
  }

  getBookingTypes(diaryUuid: string) {
    return this.timeslotDiaries.filter((diary) => diary.diary_uuid === diaryUuid)?.[0]?.booking_types;
  }

  getDiary(diaryUuid: string) {
    return this.timeslotDiaries.find((diary) => diary.diary_uuid === diaryUuid);
  }

  gotoBookingPage(diaryUuid: string) {
    const firstTimeslot = this.getFirstTimeslot(diaryUuid);
    this.router.navigate([`external/diary/${diaryUuid}`], {
      queryParams: {
        bookingType: (this.bookingTypeSelectors.get(diaryUuid).value as FirstBooking).uuid,
        timeslot: firstTimeslot.toFormat('HH:mm'),
        day: firstTimeslot.toFormat('yyyy-MM-dd'),
        practice: true,
      },
    });
  }

  retry() {
    this.getPracticeAndDiaryInfo();
  }
}
